// Debug HUD v5 (M4): grouped stocks (shift-click = bump ware priority), full
// build menu incl. military + endgame, selected-building panel (priority/
// recipe/boost/buffers/garrison/recruit queues/research/monument stages),
// unit-selection panel, saves, alerts, game-over overlay. Plain TS.

import unitsJson from '../../data/units.json';
import researchJson from '../../data/research.json';
import type { AlertMsg, GameResult, InspectMsg } from '../shared/protocol';

export type UiMode =
  | { kind: 'select' }
  | { kind: 'place'; def: string; defIdx: number }
  | { kind: 'road' }
  | { kind: 'claim' }
  | { kind: 'survey' }
  | { kind: 'rally'; building: number };

/** unit names indexed by UnitKind (units.json order, slot 0 unused) */
export const UNIT_NAMES: string[] = ['', ...unitsJson.units.map((u) => u.name)];
/** kinds recruitable at a training ground (military with a weapon) */
export const RECRUIT_KINDS: number[] = unitsJson.units
  .map((u, i) => ({ u, kind: i + 1 }))
  .filter((x) => x.u.kind === 'military' && 'weapon' in x.u)
  .map((x) => x.kind);

export interface HudCallbacks {
  onSpeed(speed: 0 | 1 | 2 | 3): void;
  onNewSeed(seed: number): void;
  onMode(mode: UiMode): void;
  onTrainSurveyor(): void;
  onTrainScout(): void;
  onWarePriority(ware: number): void;
  onSetPriority(building: number, level: 0 | 1 | 2 | 3): void;
  onSetRecipe(building: number, recipe: number): void;
  onRecruit(building: number, kind: number, count: number): void;
  onVeteran(building: number, on: boolean): void;
  onUngarrison(building: number): void;
  onSetResearch(building: number, node: number): void;
  onCloseInspect(): void;
  onAlertJump(alert: AlertMsg): void;
  onSave(name: string): void;
  onLoad(name: string): void;
  onRmbToggle(on: boolean): void;
  onMenu(): void;
}

export interface HudState {
  tick: number;
  fps: number;
  simMsPerTick: number;
  checksum: number;
  seed: number;
  speed: number;
  hovered: { x: number; y: number } | null;
  stocks: number[];
  popUsed: number;
  popCap: number;
  surveyorsIdle: number;
  surveyorsTotal: number;
  openRequests: number;
  mode: UiMode;
  flowOverlay: boolean;
  inspection: InspectMsg | null;
  /** unit selection summary (main-thread selection state) */
  selection: { count: number; label: string } | null;
  /** RMB issues attack-move by default (DESIGN.md §7.5 "A-move default toggle") */
  rmbAttack: boolean;
  // ---- M4 ----
  result: GameResult | null;
  scores: [number, number];
  scoreTicksLeft: number;
  researchedMask: number;
}

const BUILD_MENU: Array<{ group: string; ids: string[] }> = [
  { group: 'infra', ids: ['storehouse', 'residence'] },
  { group: 'wood', ids: ['woodcutter', 'forester', 'sawmill', 'quarry'] },
  { group: 'food', ids: ['well', 'farm', 'windmill', 'bakery', 'fishery', 'hunter', 'ranch'] },
  { group: 'metal', ids: ['coal_mine', 'iron_mine', 'smelter', 'toolmaker'] },
  { group: 'gold', ids: ['gold_mine', 'gold_smelter', 'mint'] },
  { group: 'war', ids: ['weaponsmith', 'bowyer', 'training_ground', 'watchtower'] },
  { group: 'endgame', ids: ['athenaeum', 'monument'] },
];

const STOCK_GROUPS: string[][] = [
  ['log', 'plank', 'stone', 'tool'],
  ['wheat', 'flour', 'water', 'bread', 'fish', 'meat', 'donkey'],
  ['coal', 'iron_ore', 'iron_bar', 'gold_ore', 'gold_bar', 'coin'],
];

const PRIORITY_NAMES = ['Low', 'Normal', 'High', 'Urgent'];

export class Hud {
  private stats!: HTMLElement;
  private stocksEl!: HTMLElement;
  private toastsEl!: HTMLElement;
  private panelEl!: HTMLElement;
  private gameOverEl!: HTMLElement;
  private gameOverShown = false;
  private savesSelect!: HTMLSelectElement;
  private rmbButton!: HTMLButtonElement;
  private rmbAttackLocal = true;
  private speedButtons: HTMLButtonElement[] = [];
  private modeButtons: HTMLButtonElement[] = [];
  private toasts: Array<{ text: string; until: number; alert?: AlertMsg }> = [];
  private lastPanelKey = '';

  constructor(
    root: HTMLElement,
    seed: number,
    private wareIds: string[],
    private wareNames: string[],
    private buildingIds: string[],
    private buildingNames: string[],
    private recipeLabels: string[][],
    private cb: HudCallbacks,
  ) {
    const buildBtns = BUILD_MENU.map(
      (g) =>
        `<span class="dim">${g.group}</span>` +
        g.ids
          .map((id) => {
            const idx = this.buildingIds.indexOf(id);
            const name = this.buildingNames[idx] ?? id;
            return `<button data-mode="place" data-def="${id}" title="${name}">${name}</button>`;
          })
          .join(''),
    ).join('<span class="sep"></span>');

    root.innerHTML = `
      <div class="panel top-left">
        <div class="title">PORTAGE <span class="dim">skirmish</span>
          <button id="hud-menu" style="float:right" title="pause menu (Esc)">☰</button></div>
        <div id="hud-stats" class="stats"></div>
        <div id="hud-stocks" class="stats stocks"></div>
        <div class="row">
          <span class="dim">speed</span>
          <button data-speed="0">⏸</button>
          <button data-speed="1" class="active">1×</button>
          <button data-speed="2">2×</button>
          <button data-speed="3">3×</button>
        </div>
        <div class="row">
          <span class="dim">seed</span>
          <input id="hud-seed" type="text" value="${seed}" size="9" />
          <button id="hud-regen">regen</button>
          <button id="hud-save">save</button>
          <select id="hud-saves"><option value="">— load —</option></select>
        </div>
        <div class="dim hint">LMB: act/select (drag = box-select) · RMB: order · Esc: cancel · F: ware-flow<br>
        1-9: control groups (Ctrl+digit assigns) · -/=: speed · space: pause · shift-click stock: prioritize<br>
        MMB/WASD: pan · wheel: zoom · Q/E: rotate</div>
      </div>
      <div class="panel bottom-bar">
        <div class="row wrap">
          ${buildBtns}
          <span class="sep"></span>
          <button data-mode="road">Road</button>
          <button data-mode="claim">Claim</button>
          <button data-mode="survey">Survey</button>
          <button id="hud-train">+Surveyor</button>
          <button id="hud-scout">+Scout</button>
          <button id="hud-rmb" title="right-click default: attack-move vs move">RMB: A-move</button>
        </div>
      </div>
      <div id="hud-panel" class="panel right-panel" style="display:none"></div>
      <div id="hud-gameover" class="panel gameover" style="display:none"></div>
      <div id="hud-toasts"></div>`;

    this.stats = root.querySelector('#hud-stats')!;
    this.stocksEl = root.querySelector('#hud-stocks')!;
    this.toastsEl = root.querySelector('#hud-toasts')!;
    this.panelEl = root.querySelector('#hud-panel')!;
    this.gameOverEl = root.querySelector('#hud-gameover')!;
    this.savesSelect = root.querySelector('#hud-saves')!;

    for (const btn of root.querySelectorAll<HTMLButtonElement>('button[data-speed]')) {
      this.speedButtons.push(btn);
      btn.addEventListener('click', () =>
        this.cb.onSpeed(Number(btn.dataset.speed) as 0 | 1 | 2 | 3),
      );
    }
    for (const btn of root.querySelectorAll<HTMLButtonElement>('button[data-mode]')) {
      this.modeButtons.push(btn);
      btn.addEventListener('click', () => {
        const kind = btn.dataset.mode!;
        if (kind === 'place') {
          const def = btn.dataset.def!;
          this.cb.onMode({ kind: 'place', def, defIdx: this.buildingIds.indexOf(def) });
        } else {
          this.cb.onMode({ kind } as UiMode);
        }
      });
    }
    root.querySelector<HTMLButtonElement>('#hud-train')!.addEventListener('click', () => {
      this.cb.onTrainSurveyor();
    });
    root.querySelector<HTMLButtonElement>('#hud-scout')!.addEventListener('click', () => {
      this.cb.onTrainScout();
    });
    this.rmbButton = root.querySelector<HTMLButtonElement>('#hud-rmb')!;
    this.rmbButton.addEventListener('click', () => {
      this.rmbAttackLocal = !this.rmbAttackLocal;
      this.rmbButton.textContent = this.rmbAttackLocal ? 'RMB: A-move' : 'RMB: move';
      this.cb.onRmbToggle(this.rmbAttackLocal);
    });
    root.querySelector<HTMLButtonElement>('#hud-menu')!.addEventListener('click', () => {
      this.cb.onMenu();
    });
    root.querySelector<HTMLButtonElement>('#hud-regen')!.addEventListener('click', () => {
      const v = root.querySelector<HTMLInputElement>('#hud-seed')!.value.trim();
      const n = Number.parseInt(v, 10);
      this.cb.onNewSeed(Number.isFinite(n) ? n >>> 0 : (Date.now() & 0x7fffffff) >>> 0);
    });
    root.querySelector<HTMLButtonElement>('#hud-save')!.addEventListener('click', () => {
      const name = prompt('Save name:', 'quicksave');
      if (name) this.cb.onSave(name.trim());
    });
    this.savesSelect.addEventListener('change', () => {
      if (this.savesSelect.value) this.cb.onLoad(this.savesSelect.value);
      this.savesSelect.value = '';
    });

    this.stocksEl.addEventListener('click', (e) => {
      const chip = (e.target as HTMLElement).closest<HTMLElement>('[data-ware]');
      if (chip && e.shiftKey) {
        this.cb.onWarePriority(Number(chip.dataset.ware));
        this.toast(`${this.wareNames[Number(chip.dataset.ware)]} bumped to top priority`);
      }
    });
  }

  setSaves(names: string[]): void {
    this.savesSelect.innerHTML =
      '<option value="">— load —</option>' +
      names.map((n) => `<option value="${n}">${n}</option>`).join('');
  }

  setSeed(seed: number): void {
    const input = document.querySelector<HTMLInputElement>('#hud-seed');
    if (input) input.value = String(seed);
  }

  toast(text: string, alert?: AlertMsg): void {
    this.toasts.push({ text, until: performance.now() + 5000, alert });
    if (this.toasts.length > 5) this.toasts.shift();
    this.renderToasts();
  }

  private renderToasts(): void {
    this.toastsEl.innerHTML = this.toasts
      .map(
        (t, i) =>
          `<div class="toast ${t.alert ? 'clickable' : ''}" data-toast="${i}">${t.text}</div>`,
      )
      .join('');
    for (const el of this.toastsEl.querySelectorAll<HTMLElement>('.toast.clickable')) {
      el.addEventListener('click', () => {
        const t = this.toasts[Number(el.dataset.toast)];
        if (t?.alert) this.cb.onAlertJump(t.alert);
      });
    }
  }

  private renderPanel(ins: InspectMsg | null, selection: HudState['selection']): void {
    // unit selection takes priority over building inspection
    if (selection && selection.count > 0) {
      const key = `sel:${selection.count}:${selection.label}`;
      if (key === this.lastPanelKey) return;
      this.lastPanelKey = key;
      this.panelEl.style.display = 'block';
      this.panelEl.innerHTML = `
        <div class="row"><b>${selection.count} unit${selection.count > 1 ? 's' : ''}</b></div>
        <div>${selection.label}</div>
        <div class="dim hint">RMB: order · RMB on your watchtower: garrison<br>Ctrl+digit: assign group · digit: recall</div>`;
      return;
    }
    if (!ins) {
      this.panelEl.style.display = 'none';
      this.lastPanelKey = '';
      return;
    }
    const bufline = (label: string, buf: number[] | null): string => {
      if (!buf) return '';
      const parts: string[] = [];
      for (let w = 0; w < buf.length; w++) {
        if (buf[w] > 0) parts.push(`${this.wareNames[w]} ${buf[w]}`);
      }
      return parts.length
        ? `<div><span class="dim">${label}</span> ${parts.join(' · ')}</div>`
        : '';
    };
    const recipes = this.recipeLabels[ins.defIdx] ?? [];
    const key = JSON.stringify(ins);
    if (key === this.lastPanelKey) return; // avoid re-render (keeps buttons usable)
    this.lastPanelKey = key;
    this.panelEl.style.display = 'block';

    const stateNames = ['blueprint', 'constructing', 'awaiting worker', 'active', 'disabled'];
    const hpLine =
      ins.hp < ins.maxHp ? `<div><span class="dim">hp</span> ${ins.hp}/${ins.maxHp}</div>` : '';
    const garrisonRow =
      ins.garrisonCap > 0
        ? `<div class="row"><span class="dim">garrison</span> <b>${ins.garrison}/${ins.garrisonCap}</b>
             <button id="panel-ungarrison" ${ins.garrison === 0 ? 'disabled' : ''}>empty</button></div>
           <div class="dim hint">RMB the tower with soldiers selected to garrison</div>`
        : '';
    const trainingRows = ins.queue
      ? `<div class="row"><span class="dim">veteran</span>
           <button id="panel-vet" class="${ins.veteranMode ? 'active' : ''}">${ins.veteranMode ? 'on (+1 coin)' : 'off'}</button>
           <button id="panel-rally">rally</button></div>` +
        RECRUIT_KINDS.map(
          (k) =>
            `<div class="row"><span class="dim">${UNIT_NAMES[k]}</span>
               <button data-recruit="${k}" data-n="-1">−</button>
               <b>${ins.queue![k] ?? 0}</b>
               <button data-recruit="${k}" data-n="1">+1</button>
               <button data-recruit="${k}" data-n="5">+5</button></div>`,
        ).join('') +
        (ins.trainKind > 0
          ? `<div class="dim">training ${UNIT_NAMES[ins.trainKind]} · ${(ins.trainLeft / 10).toFixed(0)}s</div>`
          : '')
      : '';

    // Athenaeum research picker (M4, DESIGN.md §8.1)
    const isAthenaeum = this.buildingIds[ins.defIdx] === 'athenaeum';
    let researchRows = '';
    if (isAthenaeum && ins.state === 3) {
      const nodes = researchJson.nodes;
      const tierDone = (tier: number): number =>
        nodes.filter((n, i) => n.tier === tier && (ins.researchedMask & (1 << i)) !== 0).length;
      researchRows = nodes
        .map((n, i) => {
          const done = (ins.researchedMask & (1 << i)) !== 0;
          const unlocked = n.tier <= 1 || tierDone(n.tier - 1) >= researchJson.tierGate;
          const activeNode = ins.researchNode === i;
          if (done) return `<div class="dim">✓ ${n.name}</div>`;
          if (activeNode) {
            const status =
              ins.researchLeft > 0
                ? `${(ins.researchLeft / 10).toFixed(0)}s`
                : 'gathering gold bars…';
            return `<div><b>▶ ${n.name}</b> <span class="dim">${status}</span></div>`;
          }
          if (!unlocked) return `<div class="dim">🔒 ${n.name} (tier ${n.tier})</div>`;
          return `<div class="row"><button data-research="${i}">${n.name}</button>
            <span class="dim">${n.costGoldBars} bars · ${n.timeS}s</span></div>`;
        })
        .join('');
      researchRows = `<div class="dim" style="margin-top:6px">research</div>${researchRows}`;
    }

    // Monument stage progress (M4, DESIGN.md §8.2)
    let monumentRows = '';
    if (ins.stageCount > 0) {
      const needed: string[] = [];
      for (let w = 0; w < ins.cost.length; w++) {
        if (ins.cost[w] > 0) {
          needed.push(`${this.wareNames[w]} ${ins.delivered[w] ?? 0}/${ins.cost[w]}`);
        }
      }
      monumentRows =
        ins.state === 3 && ins.stage >= ins.stageCount
          ? `<div><b>Monument complete!</b></div>`
          : `<div><span class="dim">stage</span> <b>${Math.min(ins.stage + 1, ins.stageCount)}/${ins.stageCount}</b></div>` +
            (needed.length ? `<div class="dim">${needed.join(' · ')}</div>` : '');
    }
    this.panelEl.innerHTML = `
      <div class="row"><b>${this.buildingNames[ins.defIdx] ?? '?'}</b>
        <button id="panel-close" style="margin-left:auto">✕</button></div>
      <div class="dim">state ${stateNames[ins.state] ?? ins.state}${
        ins.boostTicks > 0 ? ` · boosted ${(ins.boostTicks / 10).toFixed(0)}s` : ''
      }</div>
      ${hpLine}
      ${garrisonRow}
      ${trainingRows}
      ${monumentRows}
      ${researchRows}
      <div class="row"><span class="dim">priority</span>
        ${PRIORITY_NAMES.map(
          (p, l) =>
            `<button data-prio="${l}" class="${ins.priority === l ? 'active' : ''}">${p}</button>`,
        ).join('')}
      </div>
      ${
        recipes.length > 1
          ? `<div class="row"><span class="dim">recipe</span>${recipes
              .map(
                (r, ri) =>
                  `<button data-recipe="${ri}" class="${ins.recipe === ri ? 'active' : ''}">${r}</button>`,
              )
              .join('')}</div>`
          : ''
      }
      ${bufline('in', ins.inputBuf)}
      ${bufline('out', ins.outputBuf)}
      ${bufline('stock', ins.stock)}
    `;
    this.panelEl
      .querySelector('#panel-close')!
      .addEventListener('click', () => this.cb.onCloseInspect());
    for (const btn of this.panelEl.querySelectorAll<HTMLButtonElement>('[data-prio]')) {
      btn.addEventListener('click', () =>
        this.cb.onSetPriority(ins.id, Number(btn.dataset.prio) as 0 | 1 | 2 | 3),
      );
    }
    for (const btn of this.panelEl.querySelectorAll<HTMLButtonElement>('[data-recipe]')) {
      btn.addEventListener('click', () => this.cb.onSetRecipe(ins.id, Number(btn.dataset.recipe)));
    }
    for (const btn of this.panelEl.querySelectorAll<HTMLButtonElement>('[data-recruit]')) {
      btn.addEventListener('click', () =>
        this.cb.onRecruit(ins.id, Number(btn.dataset.recruit), Number(btn.dataset.n)),
      );
    }
    for (const btn of this.panelEl.querySelectorAll<HTMLButtonElement>('[data-research]')) {
      btn.addEventListener('click', () =>
        this.cb.onSetResearch(ins.id, Number(btn.dataset.research)),
      );
    }
    this.panelEl
      .querySelector('#panel-ungarrison')
      ?.addEventListener('click', () => this.cb.onUngarrison(ins.id));
    this.panelEl
      .querySelector('#panel-vet')
      ?.addEventListener('click', () => this.cb.onVeteran(ins.id, !ins.veteranMode));
    this.panelEl
      .querySelector('#panel-rally')
      ?.addEventListener('click', () => this.cb.onMode({ kind: 'rally', building: ins.id }));
  }

  /** show the end-of-game overlay once; dismissible (the sim keeps running) */
  private renderGameOver(s: HudState): void {
    if (!s.result || this.gameOverShown) return;
    this.gameOverShown = true;
    const win = s.result.winner === 0;
    const draw = s.result.winner === -1 && s.result.reason === 'score';
    const title = draw ? 'DRAW' : win ? 'VICTORY' : 'DEFEAT';
    const why =
      s.result.reason === 'conquest'
        ? win
          ? 'Enemy headquarters destroyed.'
          : 'Your headquarters has fallen.'
        : s.result.reason === 'monument'
          ? win
            ? 'Your monument stands complete.'
            : 'The enemy monument stands complete.'
          : `Final score ${s.scores[0]} — ${s.scores[1]}.`;
    this.gameOverEl.style.display = 'block';
    this.gameOverEl.innerHTML = `
      <div class="go-title ${win ? 'win' : draw ? '' : 'lose'}">${title}</div>
      <div>${why}</div>
      <div class="dim" style="margin:6px 0 10px">score ${s.scores[0]} vs ${s.scores[1]} · tick ${s.tick}</div>
      <div class="row" style="justify-content:center">
        <button id="go-continue">keep watching</button>
        <button id="go-new">new game</button>
      </div>`;
    this.gameOverEl.querySelector('#go-continue')!.addEventListener('click', () => {
      this.gameOverEl.style.display = 'none';
    });
    this.gameOverEl.querySelector('#go-new')!.addEventListener('click', () => {
      const url = new URL(location.href);
      url.search = '';
      location.href = url.toString();
    });
  }

  update(s: HudState): void {
    for (const btn of this.speedButtons) {
      btn.classList.toggle('active', Number(btn.dataset.speed) === s.speed);
    }
    for (const btn of this.modeButtons) {
      const active =
        (s.mode.kind === 'place' && btn.dataset.def === s.mode.def) ||
        (s.mode.kind !== 'place' && btn.dataset.mode === s.mode.kind);
      btn.classList.toggle('active', active);
    }
    const hover = s.hovered ? `${s.hovered.x},${s.hovered.y}` : '—';
    const scoreLine =
      s.scoreTicksLeft >= 0
        ? ` · score <b>${s.scores[0]}</b> vs <b>${s.scores[1]}</b> · ${formatTicks(s.scoreTicksLeft)} left`
        : '';
    this.stats.innerHTML =
      `tick <b>${s.tick}</b> · fps <b>${s.fps.toFixed(0)}</b> · sim <b>${s.simMsPerTick.toFixed(2)}</b> ms · ` +
      `tile <b>${hover}</b>${s.flowOverlay ? ' · <b>flow</b>' : ''}<br>` +
      `pop <b>${s.popUsed}/${s.popCap}</b> · surveyors <b>${s.surveyorsIdle}/${s.surveyorsTotal}</b> idle · ` +
      `requests <b>${s.openRequests}</b> · crc <b>${(s.checksum >>> 0).toString(16).padStart(8, '0')}</b>${scoreLine}`;

    const rows = STOCK_GROUPS.map((group) =>
      group
        .map((id) => {
          const idx = this.wareIds.indexOf(id);
          const v = idx >= 0 ? (s.stocks[idx] ?? 0) : 0;
          return `<span class="chip" data-ware="${idx}">${id.replace('_', ' ')} <b>${v}</b></span>`;
        })
        .join(' '),
    );
    this.stocksEl.innerHTML = rows.join('<br>');

    const now = performance.now();
    const before = this.toasts.length;
    this.toasts = this.toasts.filter((t) => t.until > now);
    if (this.toasts.length !== before) this.renderToasts();

    this.renderPanel(s.inspection, s.selection);
    this.renderGameOver(s);
  }
}

/** mm:ss for a tick count (10 Hz) */
function formatTicks(ticks: number): string {
  const total = Math.floor(ticks / 10);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}
